import React, { useState, useRef, useEffect } from 'react';
import SignInForm from './SignInForm/SignInForm.tsx';
import GoogleSignInButton from './GoogleSignInButton/GoogleSignInButton.tsx';
import AppleSignInButton from './AppleSignInButton/AppleSignInButton.tsx';
import ToggleSignUpButton from './ToggleSignUpButton/ToggleSignUpButton.tsx';
import { handlePasswordReset } from './Utils.ts';
import {useAppStore} from "../../../GlobalStore/GlobalStore.ts";

interface SignInModalProps {}

const SignInModal: React.FC<SignInModalProps> = ({ }) => {
    const [isSignUp, setIsSignUp] = useState(false);
    const [isResetPassword, setIsResetPassword] = useState(false);
    const [email, setEmail] = useState('');
    const [resetStatusMessage, setResetStatusMessage] = useState('');
    const environmentObj = useAppStore((state: any) => state.environmentObj);
    const { environment } = environmentObj;
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);
    const modalRef = useRef<any>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                setDynamicStateObj({visiblePopup: null})
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const getCloseButton = () => {
        return (
            <button
                onClick={() => setDynamicStateObj({visiblePopup: null})}
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none"
                     viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"/>
                </svg>
            </button>
        )
    }

    const getSignInIntegrations = () => {
        const isAndroid = environment === 'mobile_android';
        const isVscode = environment === "vscode";

        return (
            <>
                <div className="mt-4 flex items-center justify-between">
                    <hr className="w-full border-gray-300"/>
                    <span className="px-2 text-gray-500">or</span>
                    <hr className="w-full border-gray-300"/>
                </div>
                <div className={`grid grid-cols-1 ${!isAndroid ? 'lg:grid-cols-2' : ''} gap-4 mt-4`}>
                    {(!isAndroid && !isVscode) && <AppleSignInButton closeModal={() => setDynamicStateObj({visiblePopup: null})} />}
                    <GoogleSignInButton closeModal={() => setDynamicStateObj({visiblePopup: null})} />
                </div>
            </>
        );
    }

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        const result = await handlePasswordReset(email);
        setResetStatusMessage(result ? "Password reset email sent!" : "Error sending password reset email");
    }


    const signInIntegrations = getSignInIntegrations();
    const closeButton = getCloseButton();
    return (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 p-4">
            <div ref={modalRef} className="relative w-full max-w-md lg:max-w-lg p-6 lg:p-10 bg-white rounded-lg shadow-lg m-4 sm:m-8 md:m-12">
                {closeButton}
                <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 text-gray-800">
                    {isResetPassword ? 'Reset Password' : (isSignUp ? 'Create Account' : 'Sign In')}
                </h2>

                {isResetPassword ? (
                    <form onSubmit={handleResetPassword} className="space-y-4">
                        <input
                            type="email"
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-lg text-black"
                            required
                        />
                        {resetStatusMessage && (
                            <p className={`text-sm ${resetStatusMessage.includes("Error") ? "text-red-500" : "text-green-500"}`}>
                                {resetStatusMessage}
                            </p>
                        )}
                        <button
                            type="submit"
                            className="w-full py-2 px-4 bg-cyan-800 hover:bg-cyan-700 text-white font-semibold rounded-lg"
                        >
                            Send Reset Email
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setIsResetPassword(false);
                                setResetStatusMessage('');
                            }}
                            className="w-full py-2 px-4 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg mt-2"
                        >
                            Back to Sign In
                        </button>
                    </form>
                ) : (
                    <>
                        <SignInForm isSignUp={isSignUp} closeModal={() => setDynamicStateObj({visiblePopup: null})} />
                        {signInIntegrations}
                        <ToggleSignUpButton isSignUp={isSignUp} setIsSignUp={setIsSignUp} />
                        <button
                            type="button"
                            onClick={() => setIsResetPassword(true)}
                            className="w-full py-2 px-4 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg mt-2"
                        >
                            Forgot Password?
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default SignInModal;
